import { Fragment, useEffect, useState } from "react";
import { Alert, Box, Button, Container, Dialog, DialogContent, FormControlLabel, Radio, RadioGroup, TextField, Typography } from "@mui/material";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useParams } from "react-router-dom";
import { createBlockAnswers, updateBlockAnswers } from "../axios/axiosFunctions";
import { interpolateReceiverName } from "../utils/questionText";
import { clearAnswerDraft, saveAnswerDraft } from "../utils/answerDraft";

const Questions360Section = ({blockId,questions,color,buttonsDir,changeTab,setCurrentBlock,currentAnswers,setCurrentAnswers,userEvalInfo}) => {

    const {userEvalId,userId,groupId,assessmentSlug} = useParams();
    const [msg, setMsg] = useState("");
    const [showMsg, setShowMsg] = useState(false);
    const [msgSeverity, setMsgSeverity] = useState("error");
    const [openDialog, setOpenDialog] = useState(false);
    const [missing, setMissing] = useState([]);
    const [saving, setSaving] = useState(false);

    const draftIds = {userId,userEvalId,groupId,blockId};

    // Keep a browser draft of unsaved answers so a closed tab or failed save loses nothing
    useEffect(() => {
        if(!currentAnswers[0]?._id) saveAnswerDraft(draftIds, currentAnswers);
    }, [currentAnswers]);

    const handleDialogClose = ()=>{
        setOpenDialog(false);
    }

    const handleAnswers = (e)=>{
        const {name, value} = e.target;
        if(value.trim() !== "" && missing.includes(name)) setMissing(missing.filter((id)=>id !== name));
        const findAnswer = currentAnswers.find((a)=>a.question._id === name);
        const findAnswerIndex = currentAnswers.findIndex((a)=>a.question._id === name);
        if(findAnswer){
            const newCurrentUser = [...currentAnswers];
            newCurrentUser[findAnswerIndex] = {
                ...currentAnswers[findAnswerIndex],
                answer:value
            }
            setCurrentAnswers(newCurrentUser);
        }else{
            const newCurrentUser = [...currentAnswers];
            newCurrentUser.push({
                question:{
                    _id:name
                },
                id:name,
                answer:value
            });
            setCurrentAnswers(newCurrentUser);
        }
    }

    const getRadioValue = (q)=>{
        const a = currentAnswers.find((a)=>a.question._id === q._id);
        return a?.answer || "";
    }

    const validateForm= ()=>{
        const empty = questions.filter((q)=>{
            const a = currentAnswers.find((ans)=>ans.question._id === q._id);
            return (a?.answer || "").trim() === "";
        }).map((q)=>q._id);
        setMissing(empty);
        if(empty.length){
            setMsg(empty.length === 1 ? "Please answer the highlighted question before continuing." : "Please answer the highlighted questions before continuing.");
            setMsgSeverity("error");
            setShowMsg(true);
            document.getElementById(`q360-${empty[0]}`)?.scrollIntoView({behavior:"smooth",block:"center"});
            return false;
        }
        return true;
    }

    // Returns true only when the server confirmed the save
    const saveAnswers = async()=>{
        if(!validateForm()) return false;

        const data = {
            userId : userId,
            to: userEvalId,
            blockId,
            groupId: groupId,
            answers:currentAnswers,
        }

        setSaving(true);
        const res = currentAnswers[0]?._id ? await updateBlockAnswers(data) : await createBlockAnswers(data);
        setSaving(false);

        if(!res){
            // Stay on the page with answers intact; the error stays until they try again
            setMsg("Your answers could not be saved. Please check your internet connection and click the button again. Your answers are still here.");
            setMsgSeverity("error");
            setShowMsg(true);
            return false;
        }
        clearAnswerDraft(draftIds);
        setShowMsg(false);
        return true;
    }

    const handleSubmit = async(e)=>{
        e.preventDefault();
        if(!(await saveAnswers())) return;
        setCurrentBlock(buttonsDir.after);
        window.scrollTo(0, 0);
    }

    const handleSubmitComplete = async()=>{
        setOpenDialog(false);
        if(!(await saveAnswers())) return;
        setCurrentBlock(undefined);
        changeTab(2);
    }

    const handleBackToMenu = ()=>{
        setCurrentBlock(undefined);
        changeTab(0);
    }

    return (
    <Container>
        <Box display={"flex"} sx={{marginTop:"40px",paddingBottom:"48px"}}>
            <form onSubmit={handleSubmit}>
            {
                questions?.map((q,i)=>(
                    <Fragment key={i}>
                        <Typography id={`q360-${q._id}`} variant="body1" fontWeight={600} color={missing.includes(q._id) ? "error" : color} sx={{fontSize:"1.05rem",marginTop:i > 0 ? "28px" : "0px"}}>{i+1}. {(q.typeQ || "mo") !== "op" && userId !== userEvalId ? `How confident are you that ${userEvalInfo?.firstName || ""} ${userEvalInfo?.lastName || ""} ` : ""}{interpolateReceiverName(q.text, `${userEvalInfo?.firstName || ""} ${userEvalInfo?.lastName || ""}`.trim())}</Typography>
                        {(q.typeQ || "mo") !== "op" &&
                        <RadioGroup row value={getRadioValue(q)} onChange={(e)=>handleAnswers(e,i)} sx={{justifyContent:"space-between",padding:"4px 10px 0px"}}>
                            {userId !== userEvalId && <FormControlLabel value="unable" name={q._id} control={<Radio/>} label="Unable to Respond"/>}
                            <FormControlLabel value="notConfident" name={q._id} control={<Radio/>} label="Not Confident"/>
                            <FormControlLabel value="somewhat" name={q._id} control={<Radio/>} label="Somewhat Confident"/>
                            <FormControlLabel value="confident" name={q._id} control={<Radio/>} label="Confident"/>
                            <FormControlLabel value="very" name={q._id} control={<Radio/>} label="Very Confident"/>
                        </RadioGroup>}
                        {q.typeQ === "op" &&
                        <TextField value={getRadioValue(q)} onChange={handleAnswers} error={missing.includes(q._id)} fullWidth multiline rows={6} name={q._id} sx={{marginTop:"10px"}}/>}
                        {missing.includes(q._id) && <Typography variant="body2" color="error" sx={{marginTop:"4px"}}>This question needs an answer.</Typography>}
                    </Fragment>
                ))
            }
            {
                showMsg &&
                <Alert sx={{marginTop:"20px"}} severity={msgSeverity}>
                    {msg}
                </Alert>
            }
            <Box display={"flex"} justifyContent={"space-around"} sx={{marginTop:"30px"}}>
                {
                    buttonsDir.before ?
                    <Button onClick={()=>{ setCurrentBlock(buttonsDir.before); window.scrollTo(0, 0); }} variant="contained" color="secondary" startIcon={<ArrowBackIcon className="invertIconBlue"/>} sx={{color:"white"}}>BACK</Button>
                    :
                    <Button onClick={handleBackToMenu} variant="contained" color="secondary" startIcon={<ArrowBackIcon className="invertIconBlue"/>} sx={{color:"white"}}>BACK</Button>
                }
                {
                    buttonsDir.after ?
                    <Button type='submit' disabled={saving} variant="contained" sx={{color:"white"}} endIcon={<ArrowForwardIcon className="invertIcon"/>}>NEXT</Button>
                    : 
                    <Button onClick={()=>{ if(validateForm()) setOpenDialog(true); }} disabled={saving} variant="contained" sx={{color:"white"}}>{saving ? "SAVING..." : "COMPLETE"}</Button>
                }
                <Dialog open={openDialog} onClose={handleDialogClose}>
                    <DialogContent>
                        <Typography variant='h6' sx={{textAlign:"center"}}>Are you sure you want to SUBMIT your assessment?</Typography>
                        <Typography variant='subtitle1' sx={{textAlign:"center"}}>Once you submit, you WON'T be able to change your responses.</Typography>
                        <Box sx={{display:"flex",margin:"20px",justifyContent:"center"}}>
                            <Button onClick={handleSubmitComplete} variant='contained'sx={{marginRight:"20px",justifyContent:"space-around"}}>COMPLETE</Button>
                            <Button variant='contained' onClick={handleDialogClose} color="customGray">CANCEL</Button>
                        </Box>
                    </DialogContent>
                </Dialog>
            </Box>
            </form>
        </Box>
    </Container>
    )
}

export default Questions360Section